import type { E_CountryDialCode } from '@fe/types/modules/localization/common/backend/enums/';
import { PhoneNumberUtil } from 'google-libphonenumber';

import { countryCodeMapping } from '@fe/types/modules/localization/common/client/types';
import { COUNTRIES_DATA } from '../../constants/countries';
// !NOTE: THIS WILL BE MERGED INTO VALIDATORS FILE (packages/shared/src/_helpers/Validators/Validators.ts) & RENAME THE HELPERS AS MOST OF THEM ARE THE SAME
const phoneUtil = PhoneNumberUtil.getInstance();

export function extractPhoneParts(fullPhoneNumber?: string) {
  if (!fullPhoneNumber) {
    return { countryCode: '', phoneNumber: '' };
  }

  try {
    const parsed = phoneUtil.parse(fullPhoneNumber);
    const countryCode = `+${parsed.getCountryCode()}`;
    const nationalNumber = parsed.getNationalNumber();
    const phoneNumber = nationalNumber !== undefined ? nationalNumber.toString() : '';
    return { countryCode, phoneNumber };
  } catch (e) {
    console.error('Failed to parse phone number:', fullPhoneNumber, e);
    return { countryCode: '', phoneNumber: '' };
  }
}

export const isPhoneNumberValidForRegion = (
  phoneNumber: string,
  countryCode: string | E_CountryDialCode,
): boolean => {
  try {
    const mapping = countryCodeMapping?.[countryCode];
    if (!mapping || !mapping.isoCode) {
      console.warn('Missing mapping for country code:', countryCode);
      return false;
    }

    const isoCode = mapping.isoCode.toUpperCase();
    const cleaned = phoneNumber.replace(/[^0-9]/g, '');
    const fullNumber = `${countryCode}${cleaned}`;

    const parsedNumber = phoneUtil.parse(fullNumber, isoCode);

    const possibleReason = phoneUtil.isPossibleNumberWithReason(parsedNumber);
    if (possibleReason !== PhoneNumberUtil.ValidationResult.IS_POSSIBLE) {
      console.warn(
        'Phone number is not possible:',
        PhoneNumberUtil.ValidationResult[possibleReason],
      );
      return false;
    }

    const countryCodeFromNumber = parsedNumber.getCountryCode?.();
    if (!countryCodeFromNumber) {
      console.warn('Unable to extract country code from parsed number:', fullNumber);
      return false;
    }

    const numericCountryCode = countryCodeFromNumber.toString();

    // Parsing normalizes per-country trunk prefixes (e.g. +9620795693707 -> +962795693707
    // for Jordan, where locals write numbers with a leading 0), so numbers are accepted
    // with or without the trunk prefix as long as the dial code survives parsing intact
    if (numericCountryCode !== removePlusSignFromCountryCode(countryCode).toString()) {
      console.warn('Dial code mismatch:', fullNumber, 'parsed dial code:', numericCountryCode);
      return false;
    }
    // Debug logs
    // console.log('Parsed number:', parsedNumber);
    // console.log('Is possible:', phoneUtil.isPossibleNumber(parsedNumber));
    // console.log('Is valid:', phoneUtil.isValidNumberForRegion(parsedNumber, isoCode));

    // Special case: +1 supports both US and CA
    if (numericCountryCode === '1') {
      const isValidInCA = phoneUtil.isValidNumberForRegion(parsedNumber, 'CA');
      const isValidInUS = phoneUtil.isValidNumberForRegion(parsedNumber, 'US');
      return isValidInCA || isValidInUS;
    }

    const isValidInRegion = phoneUtil.isValidNumberForRegion(parsedNumber, isoCode);
    return isValidInRegion;
  } catch (error) {
    console.error('Phone validation error:', error);
    return false;
  }
};

export const handleMissingPlusSignInCountryCode = (countryCode: string | E_CountryDialCode) =>
  !countryCode.includes('+') ? `+${countryCode}` : countryCode;

export const getPhoneNumberValidationErrorMessage = () =>
  `Phone number must be a valid one within the specified region`;

export const removePlusSignFromCountryCode = (countryCode: string | E_CountryDialCode) => {
  if (typeof countryCode === 'string' && countryCode.startsWith('+')) {
    return countryCode.slice(1);
  }
  return countryCode;
};

export const addPlusSignToCountryCode = (countryCode: string | E_CountryDialCode) => {
  if (typeof countryCode === 'string' && !countryCode.startsWith('+')) {
    return `+${countryCode}`;
  }
  return countryCode.toString();
};

export const getDialCodeFromCountryIso = (iso: string) => {
  try {
    const code = phoneUtil.getCountryCodeForRegion(iso?.toUpperCase());
    return code ? `+${code}` : undefined;
  } catch {
    return undefined;
  }
};

export const getFlagCodeFromIso = (iso?: string) => iso?.toLowerCase();

export const getCountryAcronymFromDialCode = (dialCode: string) => {
  try {
    const parsed = phoneUtil.parse(dialCode + '123456');
    return phoneUtil.getRegionCodeForNumber(parsed)?.toLowerCase();
  } catch {
    return undefined;
  }
};

export function getDialCodeByAcronym(
  acronym: string,
  fallback: string | null = null,
): string | null {
  const country = COUNTRIES_DATA.find((c) => c.isoCode.toLowerCase() === acronym.toLowerCase());
  return country?.dialCode ?? fallback;
}
